import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider } from "@clerk/react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import brandIcon from "./assets/brand-icon.png";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Don't retry reauth errors — the user has to click Reconnect.
        if (error instanceof Error && error.name === "ReauthError") return false;
        return failureCount < 2;
      },
    },
  },
});

function fatal(message: string) {
  const root = document.getElementById("root")!;
  root.innerHTML = `<div style="display:grid;place-items:center;height:100%;padding:24px;text-align:center;font:14px/1.5 -apple-system,sans-serif;color:#616161"><div><p style="font-weight:600;color:#242424">mysmall_life can't start</p><p>${message}</p></div></div>`;
}

// The Clerk publishable key is public but not build-time inlined here, so fetch
// it from a public endpoint before mounting the provider.
async function bootstrap() {
  let clerkPublishableKey = "";
  try {
    ({ clerkPublishableKey } = await fetch("/api/public-config").then((r) => r.json()));
  } catch {
    return fatal("Could not reach the server. Is it running?");
  }
  if (!clerkPublishableKey) {
    return fatal(
      "Authentication isn't configured — set VITE_CLERK_PUBLISHABLE_KEY and CLERK_SECRET_KEY in .env (see README).",
    );
  }

  createRoot(document.getElementById("root")!).render(
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      afterSignOutUrl="/"
      appearance={{
        options: {
          logoImageUrl: brandIcon,
          logoLinkUrl: "/",
          logoPlacement: "inside",
        },
        elements: {
          logoImage: { width: "48px", height: "48px", borderRadius: "8px" },
        },
      }}
    >
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ClerkProvider>,
  );
}

bootstrap();
